/**
 * Service-worker registration helper — Plan 01-08.
 *
 * Wires the browser's ServiceWorkerRegistration lifecycle into two optional
 * callbacks Plan 01-15 consumes for the update UX:
 *   - onRegistered(reg)   — first-load install landed
 *   - onNeedRefresh(reg)  — a new SW is waiting; UI should prompt/auto-reload
 *
 * Contracts (locked by sw-register.test.ts):
 *   - Runs exactly zero side effects when navigator.serviceWorker is
 *     unavailable (SSR / older browsers / jsdom default).
 *   - If registration.waiting is already present at mount (e.g. a reload
 *     after a background update), onNeedRefresh fires once.
 *   - On 'updatefound', watches the new installing worker; when it reaches
 *     'installed' AND another controller is already active, onNeedRefresh
 *     fires.
 *   - 'controllerchange' on navigator.serviceWorker triggers exactly ONE
 *     window.location.reload(), guarded by a module-level boolean so rapid
 *     successive events don't double-reload (CRIT-5 guard).
 *   - register() rejections are swallowed — Plan 15 can surface via telemetry.
 */

type Cb = (reg: ServiceWorkerRegistration) => void;

let reloading = false;

export interface RegisterSwOptions {
  onRegistered?: Cb;
  onNeedRefresh?: Cb;
}

export function registerSW(opts: RegisterSwOptions = {}): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  navigator.serviceWorker
    .register('/sw.js', { scope: '/', type: 'module' })
    .then((reg) => {
      opts.onRegistered?.(reg);

      if (reg.waiting) {
        opts.onNeedRefresh?.(reg);
      }

      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            opts.onNeedRefresh?.(reg);
          }
        });
      });
    })
    .catch(() => {
      /* Plan 15 surfaces failures via its own telemetry channel. */
    });

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

export function sendSkipWaiting(reg: ServiceWorkerRegistration): void {
  reg.waiting?.postMessage({ type: 'SKIP_WAITING' });
}

/** Test-only — resets the module-level reload guard. Do not use in app code. */
export function __resetForTests(): void {
  reloading = false;
}
